import { useState, useEffect } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';

export interface OpenFgaPermissionCheck {
  allowed: boolean;
  loading: boolean;
  error?: Error;
}

/**
 * Cache for permission checks to avoid multiple simultaneous requests
 */
const permissionCache = new Map<string, { allowed: boolean; timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds

/**
 * In-flight requests to deduplicate simultaneous permission checks
 */
const inFlightRequests = new Map<string, Promise<boolean>>();

/**
 * Hook to check OpenFGA permissions for the current entity
 * @param relation - The relation to check (e.g., 'can_manage_config')
 * @param entityType - Optional entity type override (defaults to entity kind in lowercase)
 * @returns Object with allowed, loading, and error states
 */
export function useOpenFgaPermission(
  relation: string,
  entityType?: string
): OpenFgaPermissionCheck {
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();

  useEffect(() => {
    let cancelled = false;

    const checkPermission = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // If entityType is specified and doesn't match current entity, skip check
        const currentEntityKind = entity.kind.toLowerCase();
        if (entityType && entityType !== currentEntityKind) {
          console.log(`🔐 Skipping permission check: entityType '${entityType}' doesn't match current entity '${currentEntityKind}'`);
          if (!cancelled) {
            setAllowed(false);
            setLoading(false);
          }
          return;
        }

        // Get user identity
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;
        const userName = userEntityRef.split('/')[1];

        // Get user email from catalog
        let userEmail = userName; // Fallback to username
        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) {
            userEmail = profile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch user entity, using username instead:', err);
        }

        // Check for impersonated user (for development/testing)
        const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
        const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
        const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
        
        // If impersonated, try to get email for that user too
        let effectiveUser = impersonatedUser || userEmail;
        if (impersonatedUser && impersonatedUser !== userEmail) {
          try {
            const impersonatedUserRef = `user:default/${impersonatedUser}`;
            const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
            const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
            if (impersonatedProfile?.email) {
              effectiveUser = impersonatedProfile.email;
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
          }
        }

        // Get entity reference
        const entityKind = entityType || entity.kind.toLowerCase();
        
        // Use the correct identifier based on entity type
        let entityIdentifier: string;
        
        if (entityKind === 'application') {
          // For applications, MUST use APM number in UPPERCASE - NO FALLBACK
          if (!entity.spec?.number) {
            console.error('❌ Application missing spec.number - permission denied');
            if (!cancelled) {
              setAllowed(false);
              setLoading(false);
            }
            return;
          }
          entityIdentifier = String(entity.spec.number).toUpperCase();
        } else if (entityKind === 'project') {
          // For projects, use project code/number in UPPERCASE - NO FALLBACK
          if (!entity.spec?.number) {
            console.error('❌ Project missing spec.number - permission denied');
            if (!cancelled) {
              setAllowed(false);
              setLoading(false);
            }
            return;
          }
          entityIdentifier = String(entity.spec.number).toUpperCase();
        } else if (entityKind === 'component') {
          // For components, use the normalized Backstage name (lowercase with dashes)
          entityIdentifier = entity.metadata.name;
        } else {
          // Default: use name as-is for other entity types
          entityIdentifier = entity.metadata.name;
        }
        
        const resource = `${entityKind}:${entityIdentifier}`;

        // Generate cache key
        const cacheKey = `${effectiveUser}:${resource}:${relation}`;

        // Check cache first
        const cached = permissionCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          console.log('🔐 Using cached permission for:', cacheKey, cached.allowed ? '✅' : '❌');
          if (!cancelled) {
            setAllowed(cached.allowed);
            setLoading(false);
          }
          return;
        }

        // Check if there's already an in-flight request for this permission
        let permissionAllowed: boolean;
        
        const existingRequest = inFlightRequests.get(cacheKey);
        if (existingRequest) {
          console.log('🔐 Waiting for in-flight request:', cacheKey);
          permissionAllowed = await existingRequest;
        } else {
          // Create new request and store it
          const requestPromise = (async (): Promise<boolean> => {
            try {
              // Log for debugging
              console.log('🔐 Checking OpenFGA permission:', {
                user: `user:${effectiveUser}`,
                relation,
                resource,
              });

              // Call backend API
              const backendUrl = config.getString('backend.baseUrl');
              const token = (await identityApi.getCredentials()).token;
              
              const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  user: `user:${effectiveUser}`,
                  action: relation,
                  resource: resource,
                }),
              });

              if (!response.ok) {
                throw new Error(`Permission check failed: ${response.statusText}`);
              }

              const data = await response.json();
              const allowed = data.allowed ?? false;
              
              // Update cache
              permissionCache.set(cacheKey, { allowed, timestamp: Date.now() });
              console.log(`🔐 Permission check result for ${relation}:`, allowed ? '✅ Allowed' : '❌ Denied');
              
              return allowed;
            } finally {
              // Clean up in-flight request
              inFlightRequests.delete(cacheKey);
            }
          })();

          inFlightRequests.set(cacheKey, requestPromise);
          permissionAllowed = await requestPromise;
        }

        if (!cancelled) {
          setAllowed(permissionAllowed);
        }
      } catch (err) {
        if (!cancelled) {
          const error = err as Error;
          setError(error);
          setAllowed(false);
          console.error('🔐 Permission check error:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    checkPermission();

    return () => {
      cancelled = true;
    };
  }, [entity, relation, entityType, identityApi, catalogApi, config]);

  return { allowed, loading, error };
}
